import { useState, useEffect } from "react";
import { Button, Stack, Table, Container, Row, Col, Card } from "react-bootstrap";

import supabase from '../utils/Supabase';
import { exportCsv } from "../utils/ExportCsv";
import { getDatabaseExport } from "../utils/Database";
import { getAllIndexingRecords } from "../utils/Indexing";
import { getAllFiles, softDeleteFile, getFileMD5 } from "../utils/Storage";
import { refreshIndexingRecords } from "../utils/Indexing";
import { showToast } from "../utils/Toasts";
import { formatDate } from "../utils/Dates";
import { getPatreonUserCount } from "../utils/Patreon";

import useIndexingStore from "../stores/Indexing";

import { LoadingText } from "../components/LoadingText";
import { ConfirmationModal } from "../components/Confirmation";

const patreonConfigured = !!process.env.REACT_APP_PATREON_CLIENT_ID;

export default function AdminTools() {
    const [isExporting, setIsExporting] = useState(false);

    const setFilesWithoutIndex = useIndexingStore((state) => state.setFilesWithoutIndex);

    const [isFindingOrphans, setIsFindingOrphans] = useState(false);
    const [searchDone, setSearchDone] = useState(false);
    const [foundOrphans, setFoundOrphans] = useState<any[]>([]);
    const [orphanStatus, setOrphanStatus] = useState("");

    const [isRefreshingPatreon, setIsRefreshingPatreon] = useState(false);
    const [patreonUserCount, setPatreonUserCount] = useState<number | null>(null);

    const [inAction, setInAction] = useState(false);
    const [actionDone, setActionDone] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null);

    async function getScriptFilenames(limit = 1000, offset = 0) {
        const allScripts = [];

        while (true) {
            const { data: scripts, error } = await supabase
                .from("files")
                .select("Title")
                .range(offset, offset + limit - 1);

            if (error) {
                console.error("Database error:", error);
                break;
            }

            if (!scripts || scripts.length === 0) {
                break;
            }

            allScripts.push(...scripts);
            offset += limit;
        }

        return allScripts;
    }

    async function fetchPatreonUserCount() {
        const count = await getPatreonUserCount();
        if (count !== null) {
            setPatreonUserCount(count);
        }
    }

    useEffect(() => {
        if (patreonConfigured) {
            fetchPatreonUserCount();
        }
    }, []);

    return (
        <>
            <Card style={{ maxWidth: "800px" }}>
                <Card.Body>
                    <Card.Title>Export Database</Card.Title>
                    <Card.Text className="mb-2">Download a comprehensive export of the script database.</Card.Text>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={isExporting}
                        onClick={async () => {
                            setIsExporting(true);

                            const databaseExport = await getDatabaseExport();

                            if (!databaseExport || databaseExport.length === 0) {
                                setIsExporting(false);
                                showToast("error", "Unable to export database.");
                                return;
                            }

                            exportCsv('DatabaseExport', databaseExport);
                            setIsExporting(false);
                            showToast("success", "Database export complete.");
                        }}
                    >
                        {!isExporting ? (
                            'Export CSV'
                        ) : (
                            <LoadingText text="Exporting..." className="mb-0" />
                        )}
                    </Button>
                </Card.Body>
            </Card>

            <Card className="mt-4" style={{ maxWidth: "800px" }}>
                <Card.Body>
                    <Card.Title>Find Orphaned Files</Card.Title>
                    <Card.Text className="mb-2">Check for files not currently associated with a database or indexing record.</Card.Text>
                    {isFindingOrphans && (<Card.Text>{orphanStatus}</Card.Text>)}
                    {!searchDone && (
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={isFindingOrphans}
                            onClick={async () => {
                                setIsFindingOrphans(true);
                                setSearchDone(false);
                                setFoundOrphans([]);
                                getAllFiles(setOrphanStatus).then(async returnedFiles => {
                                    await getScriptFilenames().then(async scripts => {
                                        const notInDatabase = returnedFiles.filter(file => !scripts.some((script: any) => script["Title"] === file.name));
                                        const indexingRecords = await getAllIndexingRecords();
                                        const notInIndexing = notInDatabase.filter(file => !indexingRecords.some((record: any) => record.file_name === file.name));
                                        setFoundOrphans(notInIndexing);
                                        if (notInIndexing.length > 0) {
                                            setOrphanStatus(`Found ${returnedFiles.length} total files in storage, ${notInIndexing.length} orphaned.`)
                                        };
                                        setSearchDone(true);
                                        setIsFindingOrphans(false);
                                    }).catch(error => {
                                        showToast("error", "Error fetching scripts from database.");
                                        setIsFindingOrphans(false);
                                    });
                                });
                            }}
                        >
                            {!isFindingOrphans ? (
                                'Check Storage'
                            ) : (
                                <LoadingText text="Checking..." className="mb-0" />
                            )}
                        </Button>
                    )}
                    {searchDone && (
                        <div className="mt-3">
                            {foundOrphans.length > 0 ? (
                                <>
                                    <p>{!inAction ? orphanStatus : <LoadingText text={orphanStatus} className="mb-0" />}</p>
                                    {!inAction && !actionDone && (
                                        <Container fluid>
                                            <Row>
                                                <Col>
                                                    <Table striped hover size="sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Filename</th>
                                                                <th>Created</th>
                                                                <th>Last Accessed</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {foundOrphans
                                                                .map((orphan) => (
                                                                    <tr key={orphan.id}>
                                                                        <td>{orphan.name}</td>
                                                                        <td>{formatDate(orphan.created_at)}</td>
                                                                        <td>{formatDate(orphan.last_accessed_at)}</td>
                                                                    </tr>
                                                                ))
                                                            }
                                                        </tbody>
                                                    </Table>
                                                </Col>
                                            </Row>
                                        </Container>
                                    )}
                                    {!inAction && !actionDone && (
                                        <Stack direction="horizontal" gap={2}>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => {
                                                    setSearchDone(false);
                                                    setFoundOrphans([]);
                                                    setOrphanStatus("");
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                disabled={foundOrphans.length === 0}
                                                onClick={() => {
                                                    setOrphanStatus("");
                                                    setConfirmationMessage(`Delete ${foundOrphans.length} orphaned files? Deleted files can only be recovered from the back-end storage.`);
                                                    setPendingAction(() => async () => {
                                                        setShowModal(false);
                                                        setInAction(true);
                                                        for (let [index, orphan] of foundOrphans.entries()) {
                                                            setOrphanStatus(`Deleting orphaned file ${index + 1} of ${foundOrphans.length}...`);
                                                            await softDeleteFile("files", orphan.name, true);
                                                        }
                                                        setInAction(false);
                                                        setActionDone(true);
                                                    });
                                                    setShowModal(true);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                disabled={foundOrphans.length === 0}
                                                onClick={() => {
                                                    setOrphanStatus("");
                                                    setConfirmationMessage(`Add ${foundOrphans.length} orphaned files for indexing? Make sure to confirm that files are valid before adding.`);
                                                    setPendingAction(() => async () => {
                                                        setShowModal(false);
                                                        setInAction(true);

                                                        setOrphanStatus(`Getting MD5 hashes for ${foundOrphans.length} orphaned files...`)
                                                        const filesToInsert = await Promise.all(
                                                            foundOrphans
                                                                .map(async orphan => {
                                                                    const MD5Hash = await getFileMD5(orphan.name);
                                                                    return {
                                                                        file_name: orphan.name,
                                                                        file_id: orphan.id,
                                                                        file_type: orphan.metadata.mimetype || 'unknown',
                                                                        file_hash: MD5Hash || '',
                                                                    };
                                                                })
                                                        );

                                                        if (filesToInsert.length > 0) {
                                                            await supabase
                                                                .from("indexing")
                                                                .upsert(filesToInsert);
                                                            setOrphanStatus(`Added ${filesToInsert.length} orphaned files for indexing.`)
                                                        }

                                                        const updatedIndexing = await refreshIndexingRecords();
                                                        setFilesWithoutIndex(updatedIndexing);

                                                        setInAction(false);
                                                        setActionDone(true);
                                                    });
                                                    setShowModal(true);
                                                }}
                                            >
                                                Add to Indexing
                                            </Button>
                                        </Stack>
                                    )}
                                    {actionDone && (
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => {
                                                setActionDone(false);
                                                setSearchDone(false);
                                                setFoundOrphans([]);
                                                setOrphanStatus("");
                                            }}
                                        >
                                            Done
                                        </Button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Card.Text>No orphaned files found in storage.</Card.Text>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => {
                                            setActionDone(false);
                                            setSearchDone(false);
                                            setFoundOrphans([]);
                                            setOrphanStatus("");
                                        }}
                                    >
                                        Done
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {patreonConfigured && (
                <Card className="mt-4" style={{ maxWidth: "800px" }}>
                    <Card.Body>
                        <Card.Title>Refresh All Patreon Users</Card.Title>
                        <Card.Text className="mb-2">Manually refresh permissions for all linked Patreon users.</Card.Text>
                        {patreonUserCount !== null ? (
                            patreonUserCount > 0 ? (
                                <p>There {patreonUserCount > 1 ? "are" : "is"} currently <strong>{patreonUserCount} {patreonUserCount > 1 ? "users" : "user"}</strong> with {patreonUserCount > 1 ? "linked accounts" : "a linked account"}.</p>
                            ) : (
                                <Card.Text>There aren't any users with linked Patreon accounts yet.</Card.Text>
                            )
                        ) : (
                                <Card.Text><LoadingText text="Loading count of Patreon users..." className="mb-0" /></Card.Text>
                        )}
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={isRefreshingPatreon || patreonUserCount === 0}
                            onClick={async () => {
                                setIsRefreshingPatreon(true);

                                const { data: { session } } = await supabase.auth.getSession();
                                const { data: refreshData, error: refreshError } = await supabase.functions.invoke('patreon-utility', {
                                    body: {
                                        action: 'refresh_all',
                                        user_session: session.access_token
                                    }
                                })

                                if (!refreshData || refreshError) {
                                    console.error("Error refreshing Patreon users:", refreshError);
                                    showToast("error", "Error refreshing Patreon users.");
                                } else {
                                    console.log("Patreon refresh results:", refreshData);
                                    showToast(refreshData.failed > 0 ? "warning" : "success", `Refreshed ${refreshData.total} total users, ${refreshData.failed} failed.`);
                                }

                                setIsRefreshingPatreon(false);
                            }}
                        >
                            {!isRefreshingPatreon ? (
                                'Refresh Users'
                            ) : (
                                <LoadingText text="Refreshing..." className="mb-0" />
                            )}
                        </Button>
                    </Card.Body>
                </Card>
            )}

            <ConfirmationModal
                show={showModal}
                message={confirmationMessage}
                onConfirm={pendingAction}
                onHide={() => {
                    setShowModal(false);
                    setPendingAction(null);
                }}
            />
        </>
    );
}